import { useCallback, useSyncExternalStore } from "react"
import {
  CONSENT_CHANGE_EVENT,
  hasStoredDecision,
  isPerformanceAllowed,
  setConsentDecision,
} from "./consent"
import { STORAGE_CONSENT_KEY } from "./constants"

type ConsentSnapshot = {
  /**
   * False only while rendering on the server and during hydration, where
   * localStorage cannot be read. Treat `isPerformanceAllowed` as unknown rather
   * than as a rejection until this is true.
   */
  isResolved: boolean
  isPerformanceAllowed: boolean
  /** Whether the visitor has chosen at all. False means the banner is showing. */
  hasDecided: boolean
}

const UNRESOLVED_SNAPSHOT: ConsentSnapshot = {
  isResolved: false,
  isPerformanceAllowed: false,
  hasDecided: false,
}

const UNDECIDED_SNAPSHOT: ConsentSnapshot = {
  isResolved: true,
  isPerformanceAllowed: true,
  hasDecided: false,
}

const ACCEPTED_SNAPSHOT: ConsentSnapshot = {
  isResolved: true,
  isPerformanceAllowed: true,
  hasDecided: true,
}

const REJECTED_SNAPSHOT: ConsentSnapshot = {
  isResolved: true,
  isPerformanceAllowed: false,
  hasDecided: true,
}

function getServerSnapshot(): ConsentSnapshot {
  return UNRESOLVED_SNAPSHOT
}

function getSnapshot(): ConsentSnapshot {
  // Snapshots are compared by reference, so return one of the shared constants.
  if (!hasStoredDecision()) {
    return UNDECIDED_SNAPSHOT
  }

  return isPerformanceAllowed() ? ACCEPTED_SNAPSHOT : REJECTED_SNAPSHOT
}

function subscribe(onChange: () => void): () => void {
  function onStorage(event: StorageEvent) {
    if (event.key === STORAGE_CONSENT_KEY) {
      onChange()
    }
  }

  window.addEventListener("storage", onStorage)
  window.addEventListener(CONSENT_CHANGE_EVENT, onChange)

  return () => {
    window.removeEventListener("storage", onStorage)
    window.removeEventListener(CONSENT_CHANGE_EVENT, onChange)
  }
}

export function useConsent() {
  const {
    isResolved,
    isPerformanceAllowed: performanceAllowed,
    hasDecided,
  } = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  /* Only the account form uses this — the banner and its dialog set a decision
     directly. Reads storage rather than the snapshot so it cannot act on a
     stale render. */
  const toggleConsent = useCallback(() => {
    setConsentDecision(
      isPerformanceAllowed() ? "rejected" : "accepted",
      "account"
    )
  }, [])

  return {
    isResolved,
    isPerformanceAllowed: performanceAllowed,
    hasDecided,
    toggleConsent,
  }
}
